import styled, { keyframes } from "styled-components";

/**
 * VerifyPropertyBrief — visual treatment for the conversion
 * moment on /verify.  Reads as an editorial property brief:
 * cream paper, tabular figures, hairline rules, generous
 * negative space.  The intent is "this is real data, not a
 * sales pitch" — the brief earns the homeowner's trust BEFORE
 * the form asks them to confirm and continue.
 *
 * Aesthetic direction: financial-times property brief.  Inter
 * throughout (per project memory: stay in sans), but used at
 * an editorial scale — the subject value is 3rem+ on desktop,
 * section eyebrows are tiny tracked caps, dividers are 1px
 * coral hairlines, all numbers are tabular.  The chart is the
 * widest readable element on the page; the comparison ladder
 * sits beside it on desktop and stacks on mobile.
 */

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const drawIn = keyframes`
  from { stroke-dashoffset: var(--draw-length, 1200); }
  to { stroke-dashoffset: 0; }
`;

// =============================================================
// Outer shell — single editorial card that contains every
// brief section.  The brief is intentionally one document with
// internal section rules, not a stack of cards: that's what
// gives it the "research dossier" rather than "marketing page"
// feel.
// =============================================================
export const BriefShell = styled.section`
  position: relative;
  background: linear-gradient(180deg, #fffaf3 0%, #f8f0e4 100%);
  border: 1px solid var(--seo-rule-soft);
  border-radius: var(--seo-r-lg);
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 18px 48px rgba(16, 42, 67, 0.08);
  margin: 0 0 1.6rem;
  animation: ${fadeUp} 0.55s cubic-bezier(0.2, 0.8, 0.2, 1) both;

  /* Coral top hairline + thicker side bookends — mimics the
     binding of a printed report. */
  &::before {
    content: "";
    position: absolute;
    inset: 0 0 auto 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      var(--seo-coral) 18%,
      var(--seo-coral-text) 50%,
      var(--seo-coral) 82%,
      transparent 100%
    );
    z-index: 2;
  }

  /* Faint diagonal hatch for paper texture. */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-image: repeating-linear-gradient(
      135deg,
      rgba(168, 90, 58, 0.025) 0,
      rgba(168, 90, 58, 0.025) 1px,
      transparent 1px,
      transparent 9px
    );
    z-index: 1;
  }

  & > * {
    position: relative;
    z-index: 2;
  }
`;

// =============================================================
// Top masthead — the brief's "cover page" identity strip.
// Stamps the document as a real property brief: tracked caps
// for the section name on the left, the appraisal-roll year on
// the right, hairline below.
// =============================================================
export const Masthead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1.1rem 1.8rem 0.85rem;
  border-bottom: 1px dashed rgba(168, 90, 58, 0.25);

  .marque {
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.2em;
    color: var(--seo-coral-text);
  }
  .marque::before {
    content: "";
    width: 26px;
    height: 1px;
    background: var(--seo-coral);
    opacity: 0.7;
  }

  .roll {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    color: var(--seo-slate-mute);
    font-variant-numeric: tabular-nums;
  }

  @media (max-width: 640px) {
    padding: 0.9rem 1.1rem 0.7rem;
    flex-wrap: wrap;
    gap: 0.5rem;

    .marque,
    .roll {
      font-size: 0.65rem;
      letter-spacing: 0.16em;
    }
  }
`;

// =============================================================
// Hero — subject identity and the oversized appraised-value
// number.  Centered editorial composition; the value is the
// largest element on the page so the user immediately reads
// "$X — that's me."
// =============================================================
export const Hero = styled.header`
  padding: 1.5rem 1.8rem 1.4rem;
  text-align: center;

  .hero-compare {
    margin-top: 0.95rem;
    display: inline-flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 0.35rem 0.85rem;
    font-size: 0.88rem;
    color: var(--seo-slate);
    line-height: 1.45;
    letter-spacing: -0.003em;
    max-width: 64ch;
  }
  .hero-compare strong {
    color: var(--seo-navy);
    font-weight: 700;
  }
  .hero-compare .hc-divider {
    color: var(--seo-rule);
    font-weight: 700;
  }
  .hero-compare .hc-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.32rem;
    padding: 0.18rem 0.55rem 0.2rem;
    border-radius: 999px;
    background: ${(p) => "var(--seo-coral-soft, rgba(252, 234, 224, 0.7))"};
    border: 1px solid rgba(168, 90, 58, 0.28);
    color: var(--seo-coral-text);
    font-weight: 700;
    font-size: 0.78rem;
    font-variant-numeric: tabular-nums;
  }
  .hero-compare .hc-pill.below {
    background: rgba(214, 240, 226, 0.78);
    border-color: rgba(29, 107, 70, 0.28);
    color: #1d6b46;
  }

  .subject-address {
    font-size: clamp(1.35rem, 1.4vw + 0.95rem, 1.7rem);
    font-weight: 700;
    color: var(--seo-navy);
    line-height: 1.18;
    letter-spacing: -0.024em;
    margin: 0 0 0.35rem;
    text-wrap: balance;
  }

  .subject-area {
    font-size: 0.88rem;
    color: var(--seo-slate);
    letter-spacing: -0.003em;
    margin: 0 0 1.5rem;
  }
  .subject-area strong {
    color: var(--seo-navy);
    font-weight: 600;
  }
  .subject-area .dot {
    color: var(--seo-rule);
    margin: 0 0.45rem;
    font-weight: 700;
  }

  .value-stack {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 0.55rem;
    padding: 0 1rem;
  }

  .value {
    font-size: clamp(2.4rem, 4.2vw + 0.4rem, 3.6rem);
    font-weight: 800;
    color: var(--seo-navy);
    line-height: 0.95;
    letter-spacing: -0.04em;
    font-variant-numeric: tabular-nums;
  }

  .value-meta {
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: var(--seo-slate-mute);
    padding-top: 0.55rem;
    position: relative;
  }
  .value-meta::before {
    content: "";
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 36px;
    height: 1px;
    background: var(--seo-rule);
  }

  @media (max-width: 640px) {
    padding: 1.2rem 1rem 1rem;
  }
`;

export const DeltaChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.32rem 0.85rem 0.34rem;
  border-radius: 999px;
  font-size: 0.86rem;
  font-weight: 600;
  letter-spacing: -0.005em;
  font-variant-numeric: tabular-nums;
  border: 1px solid
    ${(p) =>
      p.$direction === "down"
        ? "rgba(29, 107, 70, 0.32)"
        : "rgba(168, 90, 58, 0.32)"};
  color: ${(p) =>
    p.$direction === "down" ? "#1d6b46" : "var(--seo-coral-text)"};
  background: ${(p) =>
    p.$direction === "down"
      ? "rgba(214, 240, 226, 0.65)"
      : "rgba(252, 234, 224, 0.65)"};
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.7) inset;

  .arrow {
    font-size: 0.75rem;
    line-height: 1;
  }
  .pct {
    font-weight: 700;
  }
  .qualifier {
    color: var(--seo-slate);
    font-weight: 500;
    letter-spacing: -0.002em;
  }
  .sep {
    color: var(--seo-rule);
    font-weight: 700;
  }
`;

// =============================================================
// Section block — a generic editorial-section wrapper with a
// labeled rule.  Reused across the comparison ladder, chart,
// distribution strip, and protest-signal sections.
// =============================================================
export const Section = styled.section`
  padding: 1.4rem 1.8rem;
  border-top: 1px dashed rgba(168, 90, 58, 0.18);

  &:first-child {
    border-top: none;
  }

  @media (max-width: 640px) {
    padding: 1.1rem 1.1rem;
  }
`;

export const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin: 0 0 1rem;

  .stamp {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    font-size: 0.66rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.2em;
    color: var(--seo-coral-text);
    white-space: nowrap;
  }
  .stamp::before {
    content: "";
    width: 18px;
    height: 1px;
    background: var(--seo-coral);
    opacity: 0.7;
  }

  .stamp-number {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: rgba(252, 234, 224, 0.85);
    border: 1px solid rgba(168, 90, 58, 0.42);
    color: var(--seo-coral-text);
    font-size: 0.72rem;
    font-weight: 800;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-variant-numeric: tabular-nums;
  }

  .stamp-rule {
    flex: 1;
    height: 1px;
    background: linear-gradient(
      90deg,
      rgba(168, 90, 58, 0.18) 0%,
      transparent 100%
    );
  }
`;

export const SectionHeading = styled.h3`
  margin: 0 0 0.45rem !important;
  font-size: 1.05rem !important;
  font-weight: 700 !important;
  color: var(--seo-navy);
  letter-spacing: -0.016em;
  line-height: 1.3;
`;

export const SectionSub = styled.p`
  margin: 0 0 1.1rem !important;
  font-size: 0.9rem !important;
  color: var(--seo-slate);
  line-height: 1.55;
  letter-spacing: -0.003em;
  max-width: 64ch;
`;

// =============================================================
// PowerStrip — the dramatic 4-KPI band that sits between the
// hero and the trajectory chart.  Reads like a magazine
// pull-quote spread: four big numbers, each with a tiny eyebrow
// + caption.  Replaces a stack of full-width "facts" with one
// dense, visually balanced row.
// =============================================================
export const PowerStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0;
  border: 1px solid var(--seo-rule);
  border-radius: var(--seo-r-lg);
  overflow: hidden;
  background:
    linear-gradient(180deg, rgba(255, 250, 243, 0.6) 0%, rgba(245, 239, 230, 0.4) 100%),
    var(--seo-paper);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.85) inset,
    0 10px 28px rgba(16, 42, 67, 0.05);

  @media (max-width: 880px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const PowerCell = styled.div`
  position: relative;
  padding: 1.15rem 1.2rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.32rem;
  min-height: 138px;
  border-right: 1px solid var(--seo-rule);

  &:last-child {
    border-right: none;
  }

  @media (max-width: 880px) {
    &:nth-child(2) { border-right: none; }
    &:nth-child(1), &:nth-child(2) { border-bottom: 1px solid var(--seo-rule); }
  }
  @media (max-width: 480px) {
    border-right: none;
    border-bottom: 1px solid var(--seo-rule);
    min-height: 108px;
    &:last-child { border-bottom: none; }
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${(p) =>
      p.$tone === "growth"
        ? "linear-gradient(90deg, var(--seo-coral), var(--seo-coral-text))"
        : p.$tone === "compare"
          ? "linear-gradient(90deg, #b5a7e3, #7a4dd7)"
          : p.$tone === "ratio"
            ? "linear-gradient(90deg, #9bc7b1, #1d6b46)"
            : "linear-gradient(90deg, var(--seo-rule), var(--seo-slate-mute))"};
    opacity: 0.7;
  }

  .ps-eyebrow {
    font-size: 0.62rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.2em;
    color: ${(p) =>
      p.$tone === "growth"
        ? "var(--seo-coral-text)"
        : p.$tone === "compare"
          ? "#5b3aa3"
          : p.$tone === "ratio"
            ? "#1d6b46"
            : "var(--seo-slate-mute)"};
  }

  .ps-figure {
    display: flex;
    align-items: baseline;
    gap: 0.32rem;
    margin-top: 0.1rem;
    line-height: 1;
  }
  .ps-figure .ps-arrow {
    font-size: 0.95rem;
    font-weight: 800;
    color: ${(p) =>
      p.$direction === "down" ? "#1d6b46" : "var(--seo-coral-text)"};
  }
  .ps-figure .ps-number {
    font-size: clamp(1.65rem, 2.1vw + 0.5rem, 2.15rem);
    font-weight: 800;
    color: var(--seo-navy);
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.034em;
  }
  .ps-figure .ps-unit {
    font-size: 0.95rem;
    font-weight: 700;
    color: var(--seo-slate);
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.012em;
  }

  .ps-caption {
    font-size: 0.78rem;
    color: var(--seo-slate);
    line-height: 1.45;
    letter-spacing: -0.004em;
    margin-top: 0.15rem;
  }
  .ps-caption strong {
    color: var(--seo-navy);
    font-weight: 700;
  }

  /* Mini sparkline rendered in the bottom of the cell */
  .ps-spark {
    margin-top: auto;
    padding-top: 0.55rem;
  }
  .ps-spark svg {
    width: 100%;
    height: 26px;
    display: block;
  }
`;

// =============================================================
// SpreadGrid — magazine-style 2-column grid for the comparison
// ladder (left) and YoY distribution (right).  On mobile it
// stacks; on desktop they read as one balanced spread.
// =============================================================
export const SpreadGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1.25rem;
  align-items: start;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

// Inner card used inside the SpreadGrid; same paper surface
// as ChartFrame for visual consistency.
export const SpreadCard = styled.div`
  background: var(--seo-paper);
  border: 1px solid var(--seo-rule);
  border-radius: var(--seo-r-md);
  padding: 1.05rem 1.1rem 1.1rem;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  min-height: 100%;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.85) inset;

  .spread-head {
    display: flex;
    flex-direction: column;
    gap: 0.18rem;
    padding-bottom: 0.55rem;
    border-bottom: 1px dashed var(--seo-rule);
  }
  .spread-head .spread-eyebrow {
    font-size: 0.66rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    color: var(--seo-coral-text);
  }
  .spread-head .spread-title {
    font-size: 0.96rem;
    font-weight: 700;
    color: var(--seo-navy);
    letter-spacing: -0.012em;
    line-height: 1.3;
  }
  .spread-head .spread-sub {
    font-size: 0.8rem;
    color: var(--seo-slate);
    line-height: 1.45;
    letter-spacing: -0.003em;
  }
`;

// Highlighted "your home lands in this bucket" indicator that
// sits below the DistributionBar.  Pulses faintly to draw the
// eye to the comparison without being garish.
export const LandingPin = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.55rem 0.75rem;
  margin-top: 0.55rem;
  border-radius: 10px;
  background: ${(p) =>
    p.$tone === "favorable"
      ? "rgba(214, 240, 226, 0.85)"
      : p.$tone === "neutral"
        ? "rgba(241, 234, 222, 0.85)"
        : "rgba(252, 234, 224, 0.85)"};
  border: 1px solid
    ${(p) =>
      p.$tone === "favorable"
        ? "rgba(29, 107, 70, 0.32)"
        : p.$tone === "neutral"
          ? "var(--seo-rule)"
          : "rgba(168, 90, 58, 0.32)"};
  font-size: 0.84rem;
  color: var(--seo-slate);
  line-height: 1.45;
  letter-spacing: -0.003em;

  .lp-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${(p) =>
      p.$tone === "favorable"
        ? "#1d6b46"
        : p.$tone === "neutral"
          ? "var(--seo-slate-mute)"
          : "var(--seo-coral-text)"};
    box-shadow: 0 0 0 4px
      ${(p) =>
        p.$tone === "favorable"
          ? "rgba(29, 107, 70, 0.16)"
          : p.$tone === "neutral"
            ? "rgba(100, 116, 139, 0.16)"
            : "rgba(168, 90, 58, 0.16)"};
    flex: 0 0 auto;
  }
  .lp-text strong {
    color: ${(p) =>
      p.$tone === "favorable"
        ? "#1d6b46"
        : p.$tone === "neutral"
          ? "var(--seo-navy)"
          : "var(--seo-coral-text)"};
    font-weight: 800;
  }
`;

// =============================================================
// Comparison ladder — four-row visual:
//   You  ████████████████████████████  $720,000  (subject)
//   Nbhd ████████████████              $612,000  Yours +18%
//   ZIP  █████████████████████         $688,500  Yours +5%
//   Cty  ███████████                   $465,000  Yours +55%
// Each track is the same width; the bar is proportional to
// the row's value relative to the max in the set.
// =============================================================
export const LadderRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

export const LadderRow = styled.div`
  display: grid;
  grid-template-columns: minmax(140px, 0.9fr) minmax(0, 2.3fr) auto;
  gap: 0.85rem;
  align-items: center;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    gap: 0.4rem;
    padding-bottom: 0.45rem;
    border-bottom: 1px dashed var(--seo-rule);
  }

  .label {
    display: flex;
    flex-direction: column;
    gap: 0.12rem;
    line-height: 1.25;
  }
  .label .name {
    font-size: 0.95rem;
    font-weight: ${(p) => (p.$subject ? 800 : 600)};
    color: ${(p) =>
      p.$subject ? "var(--seo-coral-text)" : "var(--seo-navy)"};
    letter-spacing: -0.006em;
  }
  .label .sub {
    font-size: 0.74rem;
    color: var(--seo-slate-mute);
    line-height: 1.35;
    font-variant-numeric: tabular-nums;
  }

  .track {
    height: 18px;
    background: rgba(241, 234, 222, 0.75);
    border-radius: 999px;
    overflow: hidden;
    border: 1px solid var(--seo-rule);
    position: relative;
  }
  .track .bar {
    height: 100%;
    border-radius: 999px;
    transition: width 0.45s cubic-bezier(0.3, 0.8, 0.2, 1);
    background: ${(p) =>
      p.$subject
        ? "linear-gradient(90deg, var(--seo-coral) 0%, var(--seo-coral-text) 100%)"
        : p.$tone === "zip"
          ? "linear-gradient(90deg, #b5a7e3 0%, #7a4dd7 100%)"
          : p.$tone === "county"
            ? "linear-gradient(90deg, #9bc7b1 0%, #1d6b46 100%)"
            : "linear-gradient(90deg, #f0c5b1 0%, #c9886e 100%)"};
    box-shadow: ${(p) =>
      p.$subject ? "0 1px 0 rgba(255, 255, 255, 0.4) inset" : "none"};
  }
  .track .tick {
    position: absolute;
    top: -6px;
    bottom: -6px;
    width: 2px;
    background: rgba(16, 42, 67, 0.18);
  }

  .figure {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.18rem;
    text-align: right;

    @media (max-width: 720px) {
      align-items: flex-start;
      text-align: left;
    }
  }
  .figure .amount {
    font-size: 1.02rem;
    font-weight: 700;
    color: var(--seo-navy);
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.012em;
    line-height: 1.1;
  }
  .figure .delta {
    font-size: 0.74rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.004em;
    padding: 0.14rem 0.5rem;
    border-radius: 999px;
  }
  .figure .delta.above {
    background: rgba(252, 234, 224, 0.85);
    border: 1px solid rgba(168, 90, 58, 0.35);
    color: var(--seo-coral-text);
  }
  .figure .delta.below {
    background: rgba(214, 240, 226, 0.85);
    border: 1px solid rgba(29, 107, 70, 0.32);
    color: #1d6b46;
  }
  .figure .delta.even {
    background: rgba(241, 234, 222, 0.85);
    border: 1px solid var(--seo-rule);
    color: var(--seo-slate);
  }
`;

// =============================================================
// Trajectory chart — the centerpiece.  Big readable axes,
// multi-line overlays (subject + neighborhood + zip), endpoint
// label, hover tooltip.  This is the chart the user asked us
// to make "actually readable."
// =============================================================
export const ChartFrame = styled.div`
  background:
    radial-gradient(
      120% 80% at 0% 0%,
      rgba(252, 234, 224, 0.55) 0%,
      transparent 55%
    ),
    radial-gradient(
      100% 90% at 100% 100%,
      rgba(214, 240, 226, 0.4) 0%,
      transparent 60%
    ),
    var(--seo-paper);
  border: 1px solid var(--seo-rule);
  border-radius: var(--seo-r-lg);
  padding: 1.4rem 1.5rem 1.1rem;
  position: relative;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 12px 32px rgba(16, 42, 67, 0.06);

  &::before {
    content: "";
    position: absolute;
    inset: 0 0 auto 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      var(--seo-coral) 22%,
      var(--seo-coral-text) 50%,
      var(--seo-coral) 78%,
      transparent 100%
    );
    opacity: 0.65;
    border-top-left-radius: var(--seo-r-lg);
    border-top-right-radius: var(--seo-r-lg);
  }

  svg {
    width: 100%;
    height: auto;
    display: block;
  }

  /* Smooth draw-in for the subject line on first mount. */
  svg path.draw {
    stroke-dasharray: var(--draw-length, 1200);
    animation: ${drawIn} 1.4s cubic-bezier(0.2, 0.8, 0.2, 1) 0.15s both;
  }

  @media (max-width: 640px) {
    padding: 1rem 0.9rem 0.85rem;
  }
`;

// Header row INSIDE the ChartFrame — title on the left, the
// "delta from start to end" KPI on the right.  Gives the
// chart its own framing card identity rather than relying on
// the surrounding Section's heading to anchor it.
export const ChartHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.2rem;
  margin-bottom: 0.8rem;
  flex-wrap: wrap;

  .lhs {
    display: flex;
    flex-direction: column;
    gap: 0.18rem;
  }
  .lhs .label {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    color: var(--seo-coral-text);
  }
  .lhs .title {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--seo-navy);
    letter-spacing: -0.014em;
    line-height: 1.3;
  }

  .rhs {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.12rem;
    padding: 0.4rem 0.85rem 0.5rem;
    border-radius: 10px;
    background: rgba(252, 234, 224, 0.7);
    border: 1px solid rgba(168, 90, 58, 0.32);
  }
  .rhs .rhs-kpi {
    font-size: 1.35rem;
    font-weight: 800;
    color: var(--seo-coral-text);
    line-height: 1;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.02em;
  }
  .rhs .rhs-meta {
    font-size: 0.66rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: var(--seo-coral-text);
    opacity: 0.85;
  }

  @media (max-width: 640px) {
    align-items: flex-start;
    .rhs {
      align-self: stretch;
      align-items: flex-start;
    }
  }
`;

export const ChartLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 1.1rem;
  margin-top: 0.85rem;
  padding-top: 0.8rem;
  border-top: 1px dashed var(--seo-rule);
  font-size: 0.78rem;
  color: var(--seo-slate);
  letter-spacing: -0.003em;

  .item {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-variant-numeric: tabular-nums;
  }
  .swatch {
    display: inline-block;
    width: 22px;
    height: 3px;
    border-radius: 2px;
  }
  .swatch.subject {
    background: var(--seo-coral-text);
  }
  .swatch.neighborhood {
    background: #1d6b46;
    background-image: linear-gradient(
      90deg,
      #1d6b46 0,
      #1d6b46 4px,
      transparent 4px,
      transparent 8px
    );
    background-size: 8px 3px;
  }
  .swatch.zip {
    background: #7a4dd7;
    background-image: radial-gradient(
      circle at 1px center,
      #7a4dd7 1px,
      transparent 1.5px
    );
    background-size: 4px 3px;
  }
`;

export const ChartTooltip = styled.div`
  position: absolute;
  background: var(--seo-paper);
  border: 1px solid var(--seo-rule);
  border-radius: 8px;
  padding: 0.55rem 0.7rem 0.65rem;
  box-shadow: 0 6px 18px rgba(16, 42, 67, 0.12);
  font-size: 0.78rem;
  color: var(--seo-slate);
  pointer-events: none;
  min-width: 140px;
  transform: translate(-50%, calc(-100% - 12px));

  .tip-year {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--seo-slate-mute);
  }
  .tip-value {
    display: block;
    margin-top: 0.18rem;
    font-size: 1.08rem;
    font-weight: 800;
    color: var(--seo-navy);
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.018em;
  }
  .tip-yoy {
    display: inline-block;
    margin-top: 0.35rem;
    font-size: 0.74rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
  .tip-yoy.up {
    color: var(--seo-coral-text);
  }
  .tip-yoy.down {
    color: #1d6b46;
  }
`;

// =============================================================
// Distribution strip — stacked horizontal bar showing how the
// zip's homes moved this year.  Five segments + labels.
//
//   |▮▮▮▮▮▮▮▮▮▮▮▮▮▮▮|▮▮▮|▮▮▮▮|▮▮▮▮▮|▮▮|
//   −X% to flat   small medium    big
// =============================================================
export const DistributionFrame = styled.div`
  background: var(--seo-paper);
  border: 1px solid var(--seo-rule);
  border-radius: var(--seo-r-md);
  padding: 1.05rem 1.1rem 1.15rem;
`;

export const DistributionBar = styled.div`
  display: flex;
  height: 38px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--seo-rule);
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.6) inset;
`;

export const DistributionSeg = styled.div`
  flex: ${(p) => Math.max(p.$pct || 0, 0)} 0 0;
  background: ${(p) => p.$color};
  position: relative;
  min-width: ${(p) => ((p.$pct || 0) > 0 ? "32px" : "0")};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: filter 0.15s ease;
  cursor: default;

  .seg-pct {
    font-size: 0.82rem;
    font-weight: 800;
    color: ${(p) => p.$fg || "#fff"};
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.012em;
    text-shadow: ${(p) =>
      p.$fg && p.$fg !== "#fff"
        ? "none"
        : "0 1px 0 rgba(16, 42, 67, 0.12)"};
  }

  &:hover {
    filter: brightness(1.05);
  }

  & + & {
    border-left: 1px solid rgba(255, 255, 255, 0.45);
  }
`;

export const DistributionKey = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.4rem;
  margin-top: 0.75rem;
  font-size: 0.73rem;
  color: var(--seo-slate);
  letter-spacing: -0.003em;

  .key-cell {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    line-height: 1.35;
    padding-top: 0.4rem;
    border-top: 2px solid ${(p) => p.$color};
    /* The color comes from each individual cell, not the parent */
  }
  .key-name {
    font-weight: 600;
    color: var(--seo-navy);
  }
  .key-meta {
    font-variant-numeric: tabular-nums;
    color: var(--seo-slate-mute);
  }

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.55rem 0.65rem;
  }
`;

export const DistributionKeyCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  line-height: 1.35;
  padding-top: 0.4rem;
  border-top: 2px solid ${(p) => p.$color};
  font-size: 0.73rem;
  color: var(--seo-slate);

  .key-name {
    font-weight: 600;
    color: var(--seo-navy);
  }
  .key-meta {
    font-variant-numeric: tabular-nums;
    color: var(--seo-slate-mute);
  }
`;

// =============================================================
// Protest signal — the "killer fact" callout.  Highlighted box
// drawing the eye to the sale-to-appraisal ratio (the strongest
// single indicator of whether protests in this area pay off).
// =============================================================
export const ProtestCallout = styled.div`
  display: grid;
  grid-template-columns: minmax(160px, auto) 1fr;
  gap: 1.1rem;
  align-items: center;
  background: ${(p) =>
    p.$favorable
      ? "linear-gradient(180deg, #f0fbf4 0%, #e1f3e6 100%)"
      : "linear-gradient(180deg, #fff5ef 0%, #ffe9da 100%)"};
  border: 1px solid
    ${(p) => (p.$favorable ? "rgba(29, 107, 70, 0.28)" : "rgba(168, 90, 58, 0.28)")};
  border-left: 4px solid
    ${(p) => (p.$favorable ? "#1d6b46" : "var(--seo-coral)")};
  border-radius: var(--seo-r-md);
  padding: 1.1rem 1.3rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 0.65rem;
    padding: 0.95rem 1rem;
  }

  .ratio-block {
    display: flex;
    flex-direction: column;
    gap: 0.18rem;
    padding-right: 1.1rem;
    border-right: 1px dashed
      ${(p) => (p.$favorable ? "rgba(29, 107, 70, 0.28)" : "rgba(168, 90, 58, 0.28)")};

    @media (max-width: 640px) {
      padding-right: 0;
      border-right: none;
      padding-bottom: 0.65rem;
      border-bottom: 1px dashed
        ${(p) =>
          p.$favorable
            ? "rgba(29, 107, 70, 0.28)"
            : "rgba(168, 90, 58, 0.28)"};
    }
  }
  .ratio-label {
    font-size: 0.66rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    color: ${(p) => (p.$favorable ? "#1d6b46" : "var(--seo-coral-text)")};
  }
  .ratio-value {
    font-size: 2rem;
    font-weight: 800;
    color: var(--seo-navy);
    line-height: 1;
    letter-spacing: -0.032em;
    font-variant-numeric: tabular-nums;
  }
  .ratio-sub {
    font-size: 0.76rem;
    color: var(--seo-slate);
    line-height: 1.45;
    font-variant-numeric: tabular-nums;
  }

  .copy {
    font-size: 0.93rem;
    color: var(--seo-slate);
    line-height: 1.55;
    letter-spacing: -0.003em;
    margin: 0;
  }
  .copy strong {
    color: var(--seo-navy);
    font-weight: 700;
  }
`;

// =============================================================
// Trust footer — sources, parcel counts, refresh date.  Quiet
// editorial closer.  Lives inside the same BriefShell so the
// document feels signed-off.
// =============================================================
export const TrustFooter = styled.footer`
  padding: 1rem 1.8rem 1.25rem;
  border-top: 1px dashed rgba(168, 90, 58, 0.22);
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.4rem 1.2rem;
  font-size: 0.75rem;
  color: var(--seo-slate-mute);
  letter-spacing: -0.003em;
  line-height: 1.5;

  .lhs {
    max-width: 64ch;
  }
  .rhs {
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  @media (max-width: 640px) {
    padding: 0.85rem 1.1rem 1rem;
    font-size: 0.7rem;
  }
`;
